import { formatDistance } from "date-fns";
import { es } from 'date-fns/locale';
import { useCallback, useState } from 'react';
import { HiOutlineEye } from "react-icons/hi2";
import { IoMdLogIn } from "react-icons/io";
import { MdOutlineModeEditOutline, MdOutlineSettings } from "react-icons/md";
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ActionIcon, Loader, Switch, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { login, User } from "../../../redux/authSlice";
import { AppDispatch, RootState } from '../../../redux/store';
import { menuRoutes } from '../../../router/menu';
import { UserService } from '../../../services/user/userService';
import { UsersApiResponse } from '../../../type/entities/user-type';
import UserForm from "../components/UserForm";
import UsersFilters from './UsersFilters';
import moment from "moment";
function formatUserRoleNames(userRoles: any[] | undefined): string {
    if (!userRoles?.length) {
        return '—';
    }
    return userRoles
        .map((ur: any) => ur?.role?.name)
        .filter(Boolean)
        .join(', ');
}

const columnOrder = [
    'id',
    'profileImg',
    'name',
    'lastName',
    'dni',
    'address',
    'phone',
    'monthlySalary',
    'contractType',
    'schedule',
    'socialSecurityNumber',
    'birthDate',
    'email',
    'userRoles',
    'empresa',
    'active',
    'lastLogin',
    'createdAt',
    'updatedAt',
];

interface UsersListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    userSelected: any;
    setUserSelected: React.Dispatch<React.SetStateAction<any>>;
}

const UsersList: React.FC<UsersListProps> = ({ openFormDrawer, setOpenFormDrawer, userSelected, setUserSelected }) => {

    // STATES

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const service = new UserService();
    const dispatch = useDispatch<AppDispatch>();


    // HOOKS

    const { user } = useSelector((state: RootState) => state.auth);
    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE USUARIOS
     * @EN GETS THE LIST OF USERS
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listUsers(filters);
        return response.getResponseData() as UsersApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES TOGGLE STATUS DEL USUARIO
     * @EN TOGGLE USER STATUS
     */
    //-------------------------------------------------------------------------------------------------------------
    const toggleStatus = async (id: string, status: boolean) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleUserStatus(id, status)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(status ? "Se ha desactivado el usuario" : "Se ha activado el usuario");
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELMINACIÓN DE UN USUARIO
     * @EN HANDLES THE DELETION OF A USER
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteUser(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Usuario eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELMINACIÓN DE VARIOS USUARIOS
     * @EN HANDLES THE DELETION OF MULTIPLE USERS
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {

            const response = await (await service.deleteMultiUsers(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Usuarios eliminados correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES INICIAR SESIÓN EN LA PLATAFORMA CON EL USUARIO SELECCIONADO.
     * @EN LOGIN TO THE PLATFORM AS THE SELECTED USER.
     */
    const handleLoginAsUser = async (id: string) => {
        setIsLoading(true);
        const response = await service.loginAsUser(id) as any;
        const responseData = response.getResponseData();

        if (response && responseData?.success) {
            try {
                const user: User = {
                    id: responseData.data.user.id,
                    email: responseData.data.user.email,
                    token: responseData.data.token,
                    name: responseData.data.user.name,
                    lastName: responseData.data.user.lastName,
                    profileImage: responseData.data.user.imgProfile,
                    roles: responseData.data.user.roles,
                    companyName: responseData.data.user.companyName,
                    temporalPasswordExpiration: responseData.data.user.temporalPasswordExpiration ? true : false,
                };
                dispatch(login({
                    isAuthenticated: true,
                    loading: false,
                    error: null,
                    user: user
                }))
                toast.success('Has accedido como ' + user.name);
            } catch (error) {
                handleErrors(error);
                return;
            } finally {
                setIsLoading(false);
            }
        } else {
            handleErrors(responseData);
        }
    }
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES FORMATEA EL NOMBRE DE LA COLUMNA
     * @EN FORMATS THE COLUMN NAME
     */
    //-------------------------------------------------------------------------------------------------------------
    const capitalize = (str: string) => {
        if (typeof str !== 'string') return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            if (key === 'empresa') {
                orderedRow[key] = row.registeredCompanyLabel ?? '—';
            } else {
                orderedRow[key] = row[key];
            }
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'users-table'}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    floatingHorizontalScrollbar
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={['active', 'profileImg', 'userRoles', 'empresa']}
                    columnsNotShown={['lastName', 'updatedAt', 'address', 'phone', 'monthlySalary', 'contractType', 'schedule', 'socialSecurityNumber']}
                    overrideColumns={[

                        {
                            key: 'name',
                            render: (row: any) => {
                                const value = row.row.original.name;
                                let name = value;
                                if (row.row.original.lastName) {
                                    name = `${value} ${row.row.original.lastName}`;
                                }
                                return (
                                    <a href={`/users/${row.row.original.id}/profile`} className='text-primary hover:text-primary hover:underline'>
                                        {name}
                                    </a>
                                );
                            }

                        },
                        {
                            key: 'birthDate',
                            columnNameKey: 'Edad',
                            render: (row: any) => {
                                const birthDate = row.row.original.birthDate ? row.row.original.birthDate?.date : null;
                                if (birthDate) {
                                    const age = moment().diff(moment(birthDate), 'years');
                                    return `${age} años`;
                                }
                                return 'N/A';
                            }
                        },
                        {
                            key: 'userRoles',
                            render: (row: any) => (
                                <div>{formatUserRoleNames(row.row.original.userRoles)}</div>
                            ),
                        },
                        {
                            key: 'lastLogin',
                            render: (row: any) => {
                                const date = row.row.original?.lastLogin?.date;
                                return date ? capitalize(formatDistance(new Date(date), new Date(), { addSuffix: true, locale: es })) : 'Nunca';
                            },
                        },
                        {
                            key: 'active',
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={row.row.original.active ? 'Desactivar usuario' : 'Activar usuario'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Switch
                                            id={row.row.original.id}
                                            checked={row.row.original.active}
                                            disabled={changingStatus.includes(row.row.original.id) || row.row.original.id === user?.id}
                                            onChange={() => toggleStatus(row.row.original.id, row.row.original.active)}
                                            switchKnobClassName='bg-secondary'
                                        />
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'Log in',
                            permissions: { group: 'user', permission: 'login_as_user' },
                            render: (row: any) => {
                                if (row.id === user?.id) return null;
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Acceder como usuario'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer h-6 w-6"
                                            onClick={() => handleLoginAsUser(row.id)}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? <Loader /> :
                                                <IoMdLogIn className="h-4 w-4" size={20} />}
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            }
                        },
                        {
                            label: 'View',
                            permissions: { group: 'user', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver perfil'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.users.path}/${row.id}/profile/${menuRoutes.users.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white h-6 w-6"
                                            >
                                                <HiOutlineEye className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'user', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar usuario'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setUserSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white h-6 w-6"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'user', permission: 'edit_permissions' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar Permisos del usuario'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link to={`${menuRoutes.users.path}/${row.id}/edit/permissions`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white h-6 w-6"
                                            >
                                                <MdOutlineSettings className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'user', permission: 'delete' },
                            render: (row: any) => {
                                if (row.id === user?.id) return null;
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar usuario`}
                                                description={`¿Estás seguro de que deseas eliminar a ${row.name}?`}
                                                onDelete={() => handleDelete(row.id)}
                                                actionIconClassName="w-6 h-6"
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as UsersApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Usuarios'} onClear={() => resetFilters(50)}>
                <UsersFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    userSelected ?
                        "Completa el formulario para editar los datos del usuario" :
                        "Completa el formulario para crear un nuevo usuario en el sistema"
                }
                drawerTitle={userSelected ? 'Editar Usuario' : 'Crear Usuario'} submitText={userSelected ? 'Guardar Usuario' : 'Crear Usuario'}>
                <UserForm setOpenModal={setOpenFormDrawer} userSelected={userSelected} refetch={refetch} />
            </FormDrawerView>
        </Page>
    );
};

export default UsersList;